import React, { useCallback, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Container, Row, Col } from 'styled-bootstrap-grid';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import api from '../../../services/api';

import { useToast } from '../../../hooks/toast';

import getValidationErrors from '../../../utils/getValidationErrors';

import Header from '../../../containers/Header';
import Footer from '../../../containers/Footer';
import Breadcrumbs from '../../../containers/Breadcrumbs';

import Card from '../../../components/Card';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Loader from '../../../components/Loader';
import Dropzone from '../../../components/Dropzone';
import TextArea from '../../../components/TextArea';

import SwitchButton from '../../../components/SwitchButton';

interface AchievementData {
  id: string;
  icon: string;
  title: string;
  description: string;
  exp: number;
  key: string;
  requirement: string;
  is_hidden: boolean;
  icon_url: string;
  created_at: string;
  updated_at: string;
}

interface AchievementRegistrationFormData {
  title: string;
  description: string;
  key: string;
  exp: number;
  requirement: string;
  is_hidden: boolean;
}

const AchievementRegistration: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();
  const history = useHistory();

  const formRef = useRef<FormHandles>(null);

  const [iconFile, setCoverFile] = useState<Blob | string>('');

  const [loading, setLoading] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const handleSubmit = useCallback(
    async (data: AchievementRegistrationFormData) => {
      setLoading(true);

      formRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          title: Yup.string().required('Título obrigatório'),
          description: Yup.string().required('Descrição obrigatória'),
          key: Yup.string().required('Chave obrigatória'),
          exp: Yup.number().required('Experiência obrigatória').nullable(),
          requirement: Yup.string().required('Pré-requisito obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });

        const formData = new FormData();

        formData.append('title', data.title);
        formData.append('description', data.description);
        formData.append('key', data.key);
        formData.append('exp', String(data.exp));
        formData.append('requirement', data.requirement);
        formData.append('is_hidden', String(isHidden));
        iconFile && formData.append('icon', iconFile);

        await api.post('achievements', formData);

        addToast({
          type: 'success',
          title: 'Sucesso na atualização',
          description: 'A conquista foi atualizado com sucesso!',
        });

        history.push('/conquistas');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na atualização',
          description: 'Ocorreu um erro na atualização, verifique os campos.',
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, id, iconFile, isHidden, history],
  );

  return (
    <>
      <Header />

      <Container style={{ flex: 1 }}>
        <Breadcrumbs
          title="Visualizar conquista"
          items={[
            { title: 'Conquistas', link: '/conquistas' },
            { title: 'Visualizar conquista' },
          ]}
        />

        <Row>
          <Col md={12}>
            <Card>
              {loading && <Loader />}
              <Form ref={formRef} onSubmit={handleSubmit} noValidate>
                <Row>
                  <Col md={4}>
                    <Input type="text" name="title" label="Título" />
                  </Col>

                  <Col md={4}>
                    <Input type="text" name="key" label="Chave" />
                  </Col>

                  <Col md={4}>
                    <Input type="number" name="exp" label="Experiência" />
                  </Col>

                  <Col md={12}>
                    <SwitchButton
                      label="Oculto"
                      active={isHidden}
                      onClick={() => setIsHidden(!isHidden)}
                    />
                  </Col>

                  <Col md={6}>
                    <TextArea name="description" label="Descrição" />
                  </Col>

                  <Col md={6}>
                    <TextArea name="requirement" label="Pré-requisito" />
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Dropzone
                      label="Ícone"
                      accept="image/*"
                      maxSize={3000000}
                      onDrop={acceptedFile => setCoverFile(acceptedFile)}
                    />
                  </Col>
                </Row>

                <Button
                  type="submit"
                  color="primary"
                  style={{ marginTop: '16px' }}
                >
                  Cadastrar conquista
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>
      </Container>

      <Footer />
    </>
  );
};

export default AchievementRegistration;
